import React from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { Link, useLocation } from 'react-router-dom';
import { setSidebarOpen } from '../../features/ui/uiSlice';
import { isUser, isCompany, isAdmin, isStaff } from '../../features/auth/authSlice';
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  BellIcon,
  UserIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ChartBarIcon,
  XMarkIcon,
  ChevronRightIcon,
  SparklesIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ChartPieIcon,
  DocumentChartBarIcon,
  ChatBubbleLeftRightIcon,
  AcademicCapIcon,
  ArrowUpTrayIcon,
  CurrencyDollarIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

const Sidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { sidebarOpen } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);

  const userNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, description: 'Overview & Stats', badge: undefined },
    { name: 'Applications', href: '/dashboard/applications', icon: ClipboardDocumentListIcon, description: 'Task Applications', badge: undefined },
    { name: 'My Tasks', href: '/dashboard/tasks', icon: ClipboardDocumentListIcon, description: 'Assigned Work', badge: undefined },
    { name: 'Wallet', href: '/dashboard/wallet', icon: CreditCardIcon, description: 'Earnings & Payouts', badge: undefined },
    { name: 'Customer Support', href: '/customer-support', icon: ChatBubbleLeftRightIcon, description: 'Get Help & Support', badge: undefined },
    { name: 'Notifications', href: '/dashboard/notifications', icon: BellIcon, description: 'Alerts & Messages', badge: undefined },
    { name: 'Profile', href: '/dashboard/profile', icon: UserIcon, description: 'Account Settings', badge: undefined },
  ];

  const adminNavigation = [
    { name: 'Admin Dashboard', href: '/admin', icon: ShieldCheckIcon, description: 'System Overview', badge: 'Admin' },
    { name: 'User Management', href: '/admin/users', icon: UserGroupIcon, description: 'Manage Users', badge: undefined },
    { name: 'Staff Management', href: '/admin/staff', icon: UsersIcon, description: 'Manage Staff', badge: undefined },
    { name: 'Company Inquiries', href: '/admin/company-inquiries', icon: ChatBubbleLeftRightIcon, description: 'Manage Inquiries', badge: undefined },
    { name: 'Customer Support', href: '/customer-support', icon: ChatBubbleLeftRightIcon, description: 'Manage Support Tickets', badge: undefined },
    { name: 'Skills Management', href: '/admin/skills', icon: AcademicCapIcon, description: 'Manage Skills', badge: undefined },
    { name: 'Company Management', href: '/admin/companies', icon: BuildingOfficeIcon, description: 'Manage Companies', badge: undefined },
    { name: 'Task Management', href: '/admin/tasks', icon: ClipboardDocumentListIcon, description: 'Manage Tasks', badge: undefined },
    { name: 'Projects Management', href: '/admin/projects', icon: ClipboardDocumentListIcon, description: 'Manage Projects', badge: undefined },
    { name: 'Partners', href: '/admin/partners', icon: UserGroupIcon, description: 'Manage Partners', badge: undefined },
    { name: 'Slider Images', href: '/admin/sliders', icon: PhotoIcon, description: 'Manage Slider Images', badge: undefined },
    { name: 'Contact Submissions', href: '/admin/contacts', icon: ChatBubbleLeftRightIcon, description: 'View Contact Forms', badge: undefined },
    { name: 'Blogs Management', href: '/admin/blogs', icon: DocumentChartBarIcon, description: 'Manage Blog Posts', badge: undefined },
    { name: 'Bank Verification', href: '/admin/bank-verification', icon: CreditCardIcon, description: 'Verify Bank Details', badge: undefined },
    { name: 'Withdrawal Management', href: '/admin/withdrawals', icon: ArrowUpTrayIcon, description: 'Manage Withdrawals', badge: undefined },
    { name: 'Earnings Dashboard', href: '/admin/earnings', icon: CurrencyDollarIcon, description: 'View All Earnings', badge: undefined },
    // { name: 'Analytics', href: '/admin/analytics', icon: ChartPieIcon, description: 'Platform Analytics', badge: undefined },
    // { name: 'Reports', href: '/admin/reports', icon: DocumentChartBarIcon, description: 'Generate Reports', badge: undefined },
  ];

  const companyNavigation = [
    { name: 'Company Dashboard', href: '/company', icon: BuildingOfficeIcon, description: 'Company Overview', badge: 'Company' },
    { name: 'Team Management', href: '/company/team', icon: UsersIcon, description: 'Manage Team Members', badge: undefined },
    { name: 'Task Management', href: '/company/tasks', icon: ClipboardDocumentListIcon, description: 'Assign & Track Tasks', badge: undefined },
    { name: 'Analytics', href: '/company/analytics', icon: ChartBarIcon, description: 'Company Analytics', badge: undefined },
    { name: 'Profile', href: '/company/profile', icon: UserIcon, description: 'Company Settings', badge: undefined },
    { name: 'Notifications', href: '/dashboard/notifications', icon: BellIcon, description: 'Alerts & Messages', badge: undefined },
  ];

  const getStaffNavigation = () => {
    const baseNavigation = [
      { name: 'Staff Dashboard', href: '/staff', icon: ShieldCheckIcon, description: 'Staff Overview', badge: 'Staff' },
      { name: 'Tasks', href: '/staff/tasks', icon: ClipboardDocumentListIcon, description: 'Task Management', badge: undefined },
      // { name: 'Notifications', href: '/notifications', icon: BellIcon, description: 'Alerts & Messages', badge: undefined },
      { name: 'Profile', href: '/dashboard/profile', icon: UserIcon, description: 'Account Settings', badge: undefined },
    ];

    if (!user) return baseNavigation;

    switch (user.role) {
      case 'recruiter':
        return [
          ...baseNavigation,
          { name: 'User Management', href: '/staff/users', icon: UserGroupIcon, description: 'Manage Users', badge: undefined },
          { name: 'Staff Management', href: '/staff/staff-management', icon: UsersIcon, description: 'Manage Staff', badge: undefined },
        ];
      case 'bdm':
        return [
          ...baseNavigation,
          // { name: 'Data Validation', href: '/staff/data-validation', icon: ShieldCheckIcon, description: 'Validate Data', badge: undefined },
          // { name: 'Analytics', href: '/staff/analytics', icon: ChartBarIcon, description: 'Task Analytics', badge: undefined },
        ];
      case 'project_trainer':
        return [
          ...baseNavigation,
          { name: 'Assigned Tasks', href: '/staff/assigned-tasks', icon: ClipboardDocumentListIcon, description: 'My Assigned Tasks', badge: undefined },
        ];
      case 'crm':
        return [
          ...baseNavigation,
          { name: 'Inquiries', href: '/staff/inquiries', icon: ChatBubbleLeftRightIcon, description: 'Manage Inquiries', badge: undefined },
          { name: 'Customer Support', href: '/customer-support', icon: ChatBubbleLeftRightIcon, description: 'Manage Support Tickets', badge: undefined },
        ];
      default:
        return baseNavigation;
    }
  };

  const getNavigationItems = () => {
    if (!user) return userNavigation;
    if (isAdmin(user)) {
      return adminNavigation;
    } else if (isCompany(user)) {
      return companyNavigation;
    } else if (isStaff(user)) {
      return getStaffNavigation();
    }
    return userNavigation;
  };

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      // Dashboard should only be active on exact /dashboard path
      return location.pathname === href;
    }
    if (href === '/admin') {
      // Admin Dashboard should be active on exact /admin path
      return location.pathname === href;
    }
    if (href === '/company') {
      // Company Dashboard should be active on exact /company path
      return location.pathname === href;
    }
    if (href === '/staff') {
      // Staff Dashboard should be active on exact /staff path
      return location.pathname === href;
    }
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const getUserDisplayName = () => {
    if (!user) return 'User';
    if (isUser(user)) return `${user.firstName} ${user.lastName}`;
    if (isCompany(user)) return user.companyName;
    if (isAdmin(user)) return `${user.firstName} ${user.lastName}`;
    if (isStaff(user)) return `${user.firstName} ${user.lastName}`;
    return 'User';
  };

  const getUserInitial = () => {
    if (!user) return 'U';
    if (isUser(user)) return user.firstName?.charAt(0) || 'U';
    if (isCompany(user)) return user.companyName?.charAt(0) || 'C';
    if (isAdmin(user)) return user.firstName?.charAt(0) || 'A';
    if (isStaff(user)) return user.firstName?.charAt(0) || 'S';
    return 'U';
  };

  const getUserRole = () => {
    if (!user) return 'User';
    if (isUser(user)) return user.designation || 'User';
    if (isCompany(user)) return 'Company';
    if (isAdmin(user)) return 'Administrator';
    if (isStaff(user)) {
      switch (user.role) {
        case 'recruiter': return 'Recruiter';
        case 'bdm': return 'Business Development Manager';
        case 'project_trainer': return 'Project Trainer';
        case 'crm': return 'Customer Relationship Manager';
        default: return 'Staff';
      }
    }
    return 'User';
  };

  return (
    <>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => dispatch(setSidebarOpen(false))}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-80 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-2xl border-r border-gray-200/50 dark:border-gray-700/50 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 flex items-center justify-between h-20 px-6 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700">
          <div className="flex items-center space-x-4">
            <div className="flex items-center justify-center overflow-hidden">
              <img
                src="/logo.png"
                alt="We4Task Logo"
                className="h-full w-full object-contain p-1"
                onError={(e) => {
                  // Fallback to icon if logo fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = '<svg class="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>';
                  }
                }}
              />
            </div>
          </div>
          <button
            onClick={() => dispatch(setSidebarOpen(false))}
            className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Navigation */}
        <nav className="flex-1 px-4 py-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
          <div className="space-y-3">
            {getNavigationItems().map((item) => {
              const Icon = item.icon;
              const isItemActive = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group relative flex items-center px-4 py-4 text-sm font-medium rounded-2xl transition-all duration-300 hover:scale-[1.02] ${isItemActive
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-xl shadow-blue-500/25 transform scale-[1.02]'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white hover:shadow-lg'
                    }`}
                >
                  <div className={`flex items-center justify-center w-12 h-12 rounded-xl mr-4 transition-all duration-300 ${isItemActive
                    ? 'bg-white/20 shadow-lg'
                    : 'bg-gray-100 dark:bg-gray-800 group-hover:bg-gray-200 dark:group-hover:bg-gray-700 group-hover:shadow-md'
                    }`}>
                    <Icon
                      className={`h-6 w-6 ${isItemActive
                        ? 'text-white'
                        : 'text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200'
                        }`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold truncate text-base">{item.name}</p>
                      {item.badge && (
                        <span className={`ml-2 px-3 py-1 text-xs font-bold rounded-full ${isItemActive
                          ? 'bg-white/20 text-white'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          }`}>
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm truncate mt-1 ${isItemActive
                      ? 'text-white/80'
                      : 'text-gray-500 dark:text-gray-400'
                      }`}>
                      {item.description}
                    </p>
                  </div>
                  {isItemActive && (
                    <ChevronRightIcon className="h-5 w-5 text-white/80 ml-2" />
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Fixed User Info at Bottom */}
        <div className="flex-shrink-0 p-4 border-t border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-700">
          <div className="flex items-center p-4 bg-white/80 dark:bg-gray-800/80 rounded-2xl shadow-lg backdrop-blur-sm">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                <span className="text-lg font-bold text-white">
                  {getUserInitial()}
                </span>
              </div>
            </div>
            <div className="ml-4 flex-1 min-w-0">
              <p className="text-base font-bold text-gray-900 dark:text-white truncate">
                {getUserDisplayName()}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate font-medium">
                {getUserRole()}
              </p>
            </div>
            <div className="flex-shrink-0">
              <div className={`w-3 h-3 rounded-full shadow-lg ${user && isAdmin(user) ? 'bg-red-500' :
                user && isCompany(user) ? 'bg-green-500' :
                  'bg-blue-500'
                }`}></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;